import { useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  User,
  Lock,
  AlertTriangle,
  Megaphone,
} from 'lucide-react';
import { Button, Badge } from '@/components/ui';
import { campaignService } from '@/services/campaignService';
import { policyService } from '@/services/policyService';
import type { JourneyStep, JourneyStepStatus } from '@/types/campaign';
import type { NextStepsResponse, BlockedStep } from '@/types/policy';
import styles from './PatientJourneyPage.module.css';

type BadgeVariant = 'neutral' | 'info' | 'success' | 'warning';

const STEP_STATUS_LABELS: Record<JourneyStepStatus, string> = {
  PENDING: 'Pendente',
  SCHEDULED: 'Agendado',
  COMPLETED: 'Concluído',
  SKIPPED: 'Pulado',
};

const STEP_STATUS_BADGE: Record<JourneyStepStatus, BadgeVariant> = {
  PENDING: 'neutral',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  SKIPPED: 'warning',
};

/**
 * Patient journey visualization page.
 *
 * Shows a vertical timeline of a patient's journey through a campaign,
 * enhanced with policy evaluation to indicate:
 * - Completed steps (green checkmark)
 * - Scheduled steps (blue, with date)
 * - Blocked steps (red lock icon + reason from policy)
 * - Available next steps (green, with "Schedule" button)
 * - Policy violation warning banners
 *
 * Reuses JourneyTimeline visual patterns but adds policy-aware
 * blocked/available step annotations.
 */
export function PatientJourneyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /**
   * We fetch all campaigns and search for the journey across them.
   * The BFF endpoint for a single journey requires a campaignId,
   * so we first fetch campaigns, then find the journey.
   */
  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignService.getCampaigns(),
  });

  /**
   * Fetch all journeys from all campaigns to find the one matching
   * the route param :id. In production, a dedicated endpoint
   * GET /journeys/:id would be preferred.
   */
  const {
    data: journeyData,
    isLoading: isJourneyLoading,
    error: journeyError,
  } = useQuery({
    queryKey: ['journey', id, campaigns],
    queryFn: async () => {
      for (const campaign of campaigns) {
        const journeys = await campaignService.getJourneys(campaign.id);
        const found = journeys.find((j) => j.id === id);
        if (found) {
          return { journey: found, campaign };
        }
      }
      return null;
    },
    enabled: campaigns.length > 0 && Boolean(id),
  });

  const journey = journeyData?.journey ?? null;
  const campaign = journeyData?.campaign ?? null;

  // Fetch next steps with policy evaluation
  const { data: nextSteps } = useQuery({
    queryKey: ['nextSteps', id],
    queryFn: () => policyService.getNextSteps(id!),
    enabled: Boolean(id) && Boolean(journey),
  });

  const handleScheduleStep = useCallback(
    (stepId: string) => {
      // Navigate to schedule page with pre-filled context
      navigate(`/admin/schedule?journeyStepId=${stepId}`);
    },
    [navigate],
  );

  // Build lookup maps from next-steps data
  const availableStepIds = new Set(nextSteps?.availableStepIds ?? []);
  const blockedStepMap = new Map<string, BlockedStep>();
  if (nextSteps?.blockedSteps) {
    for (const blocked of nextSteps.blockedSteps) {
      blockedStepMap.set(blocked.stepId, blocked);
    }
  }

  // Collect policy violation warnings for display
  const policyViolations = getViolationBanners(nextSteps);

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStepDotClass = (step: JourneyStep): string => {
    if (blockedStepMap.has(step.id)) return styles.stepDotBlocked;
    if (availableStepIds.has(step.id)) return styles.stepDotAvailable;

    const dotMap: Record<JourneyStepStatus, string> = {
      PENDING: styles.stepDotPending,
      SCHEDULED: styles.stepDotScheduled,
      COMPLETED: styles.stepDotCompleted,
      SKIPPED: styles.stepDotSkipped,
    };
    return dotMap[step.status];
  };

  const getStepLineClass = (step: JourneyStep): string => {
    if (blockedStepMap.has(step.id)) return styles.stepLineBlocked;
    if (availableStepIds.has(step.id)) return styles.stepLineAvailable;

    const lineMap: Record<JourneyStepStatus, string> = {
      PENDING: styles.stepLinePending,
      SCHEDULED: styles.stepLineScheduled,
      COMPLETED: styles.stepLineCompleted,
      SKIPPED: styles.stepLineSkipped,
    };
    return lineMap[step.status];
  };

  // Loading state
  if (isJourneyLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando jornada...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (journeyError) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar jornada. Tente novamente mais tarde.</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!journey || !campaign) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Jornada não encontrada.</p>
        </div>
      </div>
    );
  }

  const sortedSteps = [...journey.steps].sort(
    (a, b) => a.orderIndex - b.orderIndex,
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link
          to={`/admin/campaigns/${campaign.id}`}
          className={styles.backLink}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Voltar para campanha
        </Link>

        <div className={styles.titleRow}>
          <div>
            <h1 className={styles.title}>Jornada do Paciente</h1>
            <p className={styles.subtitle}>{journey.patientName}</p>
          </div>
          <Badge
            variant={
              journey.status === 'COMPLETED'
                ? 'success'
                : journey.status === 'CANCELLED'
                  ? 'warning'
                  : 'info'
            }
          >
            {journey.status === 'NOT_STARTED'
              ? 'Inscrito'
              : journey.status === 'IN_PROGRESS'
                ? 'Em Andamento'
                : journey.status === 'COMPLETED'
                  ? 'Concluida'
                  : 'Cancelada'}
          </Badge>
        </div>

        <div className={styles.metaRow}>
          <span className={styles.metaItem}>
            <span className={styles.metaIcon} aria-hidden="true">
              <User size={14} />
            </span>
            {journey.patientName}
          </span>
          <span className={styles.metaItem}>
            <span className={styles.metaIcon} aria-hidden="true">
              <Megaphone size={14} />
            </span>
            {campaign.name}
          </span>
          <span className={styles.metaItem}>
            <span className={styles.metaIcon} aria-hidden="true">
              <Calendar size={14} />
            </span>
            Inscrito em {formatDateTime(journey.enrolledAt)}
          </span>
        </div>
      </header>

      {/* Policy violation warning banners */}
      {policyViolations.length > 0 && (
        <section
          className={styles.warningBanners}
          aria-label="Avisos de regras"
        >
          {policyViolations.map((violation, index) => (
            <div
              key={index}
              className={styles.warningBanner}
              role="alert"
            >
              <span className={styles.warningIcon} aria-hidden="true">
                <AlertTriangle size={16} />
              </span>
              <div className={styles.warningContent}>
                <p className={styles.warningTitle}>{violation.title}</p>
                <p className={styles.warningReason}>{violation.reason}</p>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Journey timeline */}
      <section className={styles.timelineSection}>
        <h2 className={styles.timelineSectionTitle}>Etapas da Jornada</h2>

        {sortedSteps.length === 0 ? (
          <p role="status">Nenhuma etapa cadastrada.</p>
        ) : (
          <ol aria-label="Linha do tempo da jornada" role="list">
            {sortedSteps.map((step, index) => {
              const isLast = index === sortedSteps.length - 1;
              const isBlocked = blockedStepMap.has(step.id);
              const isAvailable = availableStepIds.has(step.id);
              const blockedData = blockedStepMap.get(step.id);

              return (
                <li key={step.id} className={styles.stepCard}>
                  <div className={styles.stepIndicator} aria-hidden="true">
                    <span
                      className={[styles.stepDot, getStepDotClass(step)].join(
                        ' ',
                      )}
                    />
                    {!isLast && (
                      <span
                        className={[
                          styles.stepLine,
                          getStepLineClass(step),
                        ].join(' ')}
                      />
                    )}
                  </div>

                  <div className={styles.stepContent}>
                    <div className={styles.stepHeader}>
                      <span className={styles.stepOrder}>
                        {step.orderIndex + 1}.
                      </span>
                      <span className={styles.stepName}>
                        {step.procedureTypeName}
                      </span>
                      {isBlocked ? (
                        <Badge variant="warning">Bloqueado</Badge>
                      ) : isAvailable ? (
                        <Badge variant="success">Disponível</Badge>
                      ) : (
                        <Badge variant={STEP_STATUS_BADGE[step.status]}>
                          {STEP_STATUS_LABELS[step.status]}
                        </Badge>
                      )}
                    </div>

                    <div className={styles.stepDetails}>
                      {step.scheduledAt && (
                        <span className={styles.stepDate}>
                          Agendado: {formatDateTime(step.scheduledAt)}
                        </span>
                      )}
                      {step.completedAt && (
                        <span className={styles.stepDate}>
                          Concluído: {formatDateTime(step.completedAt)}
                        </span>
                      )}
                    </div>

                    {isBlocked && blockedData && (
                      <div className={styles.blockedInfo} role="status">
                        <span className={styles.lockIcon} aria-hidden="true">
                          <Lock size={14} />
                        </span>
                        <span>{blockedData.reason}</span>
                      </div>
                    )}

                    {isAvailable && step.status === 'PENDING' && (
                      <div className={styles.scheduleButton}>
                        <Button
                          variant="primary"
                          size="sm"
                          leftIcon={<Calendar size={14} />}
                          onClick={() => handleScheduleStep(step.id)}
                        >
                          Agendar
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}

/**
 * Extracts policy violation banners from next-steps response.
 * Shows warnings for all failed policy evaluations across blocked steps.
 */
interface ViolationBanner {
  title: string;
  reason: string;
}

function getViolationBanners(
  nextSteps: NextStepsResponse | undefined,
): ViolationBanner[] {
  if (!nextSteps?.blockedSteps) return [];

  const banners: ViolationBanner[] = [];

  for (const blocked of nextSteps.blockedSteps) {
    for (const evaluation of blocked.evaluations) {
      if (!evaluation.passed && evaluation.reason) {
        banners.push({
          title: evaluation.policyName,
          reason: evaluation.reason,
        });
      }
    }
  }

  return banners;
}
